/**
 * Dates de calendrier, au format que `<input type="date">` accepte: `yyyy-mm-dd`.
 *
 * `toISOString()` bascule en UTC avant de formater. En Tunisie (UTC+1), une
 * saisie faite apres 23h renverrait donc la veille. Les champs de ce
 * formulaire designent un jour, pas un instant: on lit le calendrier local.
 */

/** @returns {string} la date du jour, `yyyy-mm-dd`, dans le fuseau du poste. */
export function todayIsoDate() {
  return toIsoDate(new Date());
}

/**
 * Normalise ce que renvoie l'API vers `yyyy-mm-dd`.
 *
 * L'API stocke un `DateTime` et renvoie `2026-08-20T00:00:00.000Z`; un champ
 * date affiche une valeur vide si on lui donne ca. On garde les 10 premiers
 * caracteres plutot que de reconstruire la date via `new Date()`, qui
 * ramenerait le fuseau dans l'histoire et pourrait reculer d'un jour.
 *
 * @returns {string} `yyyy-mm-dd`, ou '' si la valeur n'est pas exploitable.
 */
export function toIsoDate(value) {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    const match = value.match(/^\d{4}-\d{2}-\d{2}/);
    return match ? match[0] : '';
  }

  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return '';
  }

  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');

  return `${value.getFullYear()}-${month}-${day}`;
}

/**
 * La meme date, ecrite comme on la lit en francais: `20/08/2026`.
 *
 * On repart de la forme `yyyy-mm-dd` plutot que de `new Date(...)`, pour la
 * meme raison que ci-dessus: passer par un instant ferait reculer d'un jour
 * une date rendue par l'API en UTC.
 *
 * @returns {string} la date ecrite, ou '' si la valeur n'est pas exploitable.
 */
export function toDisplayDate(value) {
  const iso = toIsoDate(value);

  if (!iso) {
    return '';
  }

  const [year, month, day] = iso.split('-');

  return `${day}/${month}/${year}`;
}

/**
 * L'exercice sur lequel porte une enquete: l'annee de la campagne.
 *
 * Une campagne menee en 2026 porte sur 2026. L'annee est enregistree avec
 * l'enquete (`referenceYear`), et c'est elle - jamais l'annee du jour ou on
 * relit l'enquete - qui libelle les champs.
 */
export function campaignYear(now = new Date()) {
  return now.getFullYear();
}

/**
 * Ce que rend un sélecteur flatpickr, ramené à `yyyy-mm-dd`.
 *
 * Selon la façon dont il est câblé, flatpickr rend une `Date`, un tableau de
 * dates, ou l'événement du champ qu'il pilote. Chaque écran de liste refaisait
 * cette lecture pour son compte; elle est ici, une fois.
 *
 * @returns {string|null} la date choisie, ou null si le champ est vidé.
 */
export function fromDatePicker(value) {
  let out = Array.isArray(value) ? value[0] : value;

  if (out && typeof out === 'object' && !(out instanceof Date)) {
    if (out.target && typeof out.target.value !== 'undefined') {
      out = out.target.value;
    } else if (typeof out.dateStr === 'string') {
      out = out.dateStr;
    } else if (out.selectedDates?.length) {
      out = out.selectedDates[0];
    }
  }

  const iso = toIsoDate(out);

  return iso || null;
}

export default {
  todayIsoDate,
  toIsoDate,
  toDisplayDate,
  campaignYear,
  fromDatePicker,
};
